import { useRef, useState, useEffect } from 'react'
import { Box, ActionIcon, Group, Text, Slider, Tooltip } from '@mantine/core'
import { IconPlayerPlay, IconPlayerPause, IconVolume, IconVolumeOff, IconMaximize } from '@tabler/icons-react'
import { getFileUrl } from '@/lib/api'
import Hls from 'hls.js'

function formatTime(sec: number) {
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

interface VideoPlayerProps {
  src: string
}

export default function VideoPlayer({ src }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [muted, setMuted] = useState(false)
  const [showControls, setShowControls] = useState(true)

  const url = getFileUrl(src)
  const isHLS = url.includes('.m3u8')

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }

    if (isHLS && Hls.isSupported()) {
      const hls = new Hls()
      hls.loadSource(url)
      hls.attachMedia(video)
      hlsRef.current = hls
    } else if (isHLS && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url
    } else {
      video.src = url
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [url, isHLS])

  const toggle = () => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) {
      v.play()
      setPlaying(true)
    } else {
      v.pause()
      setPlaying(false)
    }
  }

  const seek = (val: number) => {
    const v = videoRef.current
    if (!v) return
    v.currentTime = val
    setCurrentTime(val)
  }

  const toggleMute = () => {
    const v = videoRef.current
    if (!v) return
    v.muted = !v.muted
    setMuted(v.muted)
  }

  const fullscreen = () => {
    videoRef.current?.requestFullscreen()
  }

  return (
    <Box
      pos="relative"
      style={{ borderRadius: 8, overflow: 'hidden', background: '#000' }}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => playing && setShowControls(false)}
    >
      <video
        ref={videoRef}
        preload="metadata"
        onClick={toggle}
        onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
        onEnded={() => setPlaying(false)}
        style={{ width: '100%', display: 'block', maxHeight: 400, cursor: 'pointer' }}
      />

      {/* Overlay play button when paused */}
      {!playing && (
        <Box
          pos="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', cursor: 'pointer' }}
          onClick={toggle}
        >
          <ActionIcon variant="filled" size={56} radius="xl" color="blue">
            <IconPlayerPlay size={28} />
          </ActionIcon>
        </Box>
      )}

      {/* Controls bar */}
      {showControls && duration > 0 && (
        <Box
          pos="absolute"
          bottom={0}
          left={0}
          w="100%"
          p="xs"
          style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.8))' }}
        >
          <Slider
            value={currentTime}
            min={0}
            max={duration || 1}
            step={0.1}
            onChange={seek}
            size="xs"
            mb={4}
            label={(val) => formatTime(val)}
            styles={{ thumb: { width: 12, height: 12 }, track: { cursor: 'pointer' } }}
          />
          <Group justify="space-between">
            <Group gap={6}>
              <ActionIcon variant="subtle" color="white" size="sm" onClick={toggle}>
                {playing ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
              </ActionIcon>
              <Tooltip label={muted ? 'Дуутай' : 'Дуугүй'}>
                <ActionIcon variant="subtle" color="white" size="sm" onClick={toggleMute}>
                  {muted ? <IconVolumeOff size={16} /> : <IconVolume size={16} />}
                </ActionIcon>
              </Tooltip>
              <Text size="xs" c="white">
                {formatTime(currentTime)} / {formatTime(duration)}
              </Text>
            </Group>
            <Tooltip label="Бүтэн дэлгэц">
              <ActionIcon variant="subtle" color="white" size="sm" onClick={fullscreen}>
                <IconMaximize size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Box>
      )}
    </Box>
  )
}
